export type ProjectViewMode = 'task' | 'management';

export type ProjectDetailMainTab = 'info' | 'engineering' | 'documents' | 'issues';
export type ProjectDetailEngineeringSubTab = 'requirements' | 'components' | 'bom';

/** 任务模式下拆分后的工程主 Tab */
export type ProjectTaskModeEngTab = 'requirements' | 'components' | 'bom';

export type ProjectDynamicTab =
  | { kind: 'wbs'; id: 'wbs'; label: string }
  | { kind: 'document'; id: string; objectKey: string; fileName: string; label: string };

export type ProjectDetailTabState = {
  /** 激活 Tab：基础项 / 任务模式工程项 / 动态 id */
  activeTabId: string;
  engineeringSubTab: ProjectDetailEngineeringSubTab;
  dynamicTabs: ProjectDynamicTab[];
  /** 默认 task；管理模式无左侧任务栏 */
  viewMode: ProjectViewMode;
};

/** @deprecated 仅用于读取旧 v1 存储 */
type ProjectDetailTabStateV1 = {
  mainDetailTab: ProjectDetailMainTab;
  engineeringSubTab: ProjectDetailEngineeringSubTab;
};

const STORAGE_KEY = 'aipd:project-detail-tabs:v3';
const LEGACY_V2_KEY = 'aipd:project-detail-tabs:v2';
const LEGACY_V1_KEY = 'aipd:project-detail-tabs:v1';

const MAIN_TABS = new Set<ProjectDetailMainTab>(['info', 'engineering', 'documents', 'issues']);
const TASK_MODE_ENG_TABS = new Set<ProjectTaskModeEngTab>(['requirements', 'components', 'bom']);
const ENGINEERING_SUB_TABS = new Set<ProjectDetailEngineeringSubTab>(['requirements', 'components', 'bom']);

export const PROJECT_WBS_TAB_ID = 'wbs';

export function projectDocumentTabId(objectKey: string): string {
  return `doc:${objectKey}`;
}

export function isProjectMainTab(id: string): id is ProjectDetailMainTab {
  return MAIN_TABS.has(id as ProjectDetailMainTab);
}

export function isProjectTaskModeEngTab(id: string): id is ProjectTaskModeEngTab {
  return TASK_MODE_ENG_TABS.has(id as ProjectTaskModeEngTab);
}

export function isProjectDocumentTabId(id: string): boolean {
  return id.startsWith('doc:');
}

function readRawMap(key: string): Record<string, unknown> {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    return typeof parsed === 'object' && parsed != null ? (parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

function writeMap(map: Record<string, ProjectDetailTabState>) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // ignore quota / private mode
  }
}

function normalizeDynamicTab(raw: unknown): ProjectDynamicTab | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (o.kind === 'wbs') {
    const label = typeof o.label === 'string' && o.label.trim() ? o.label.trim() : 'WBS';
    return { kind: 'wbs', id: 'wbs', label };
  }
  if (o.kind === 'document') {
    const objectKey = typeof o.objectKey === 'string' ? o.objectKey.trim() : '';
    if (!objectKey) return null;
    const fileName = typeof o.fileName === 'string' ? o.fileName.trim() : '';
    const id = typeof o.id === 'string' && o.id.trim() ? o.id.trim() : projectDocumentTabId(objectKey);
    const label =
      typeof o.label === 'string' && o.label.trim()
        ? o.label.trim()
        : fileName || '文档查看';
    return { kind: 'document', id, objectKey, fileName, label };
  }
  return null;
}

function normalizeViewMode(raw: unknown): ProjectViewMode {
  return raw === 'management' ? 'management' : 'task';
}

function isValidActiveTabId(id: string, dynamicTabs: ProjectDynamicTab[]): boolean {
  if (isProjectMainTab(id) || isProjectTaskModeEngTab(id)) return true;
  if (id === PROJECT_WBS_TAB_ID) return true;
  return dynamicTabs.some((t) => t.id === id);
}

function normalizeState(raw: unknown): ProjectDetailTabState | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;

  // v2 / v3
  if (typeof o.activeTabId === 'string' || Array.isArray(o.dynamicTabs) || o.viewMode != null) {
    const engineeringSubTab = ENGINEERING_SUB_TABS.has(o.engineeringSubTab as ProjectDetailEngineeringSubTab)
      ? (o.engineeringSubTab as ProjectDetailEngineeringSubTab)
      : 'requirements';
    const dynamicTabs = Array.isArray(o.dynamicTabs)
      ? o.dynamicTabs.map(normalizeDynamicTab).filter((t): t is ProjectDynamicTab => !!t)
      : [];
    let activeTabId = typeof o.activeTabId === 'string' ? o.activeTabId : 'info';
    if (!isValidActiveTabId(activeTabId, dynamicTabs)) {
      activeTabId = 'info';
    }
    return {
      activeTabId,
      engineeringSubTab,
      dynamicTabs,
      viewMode: normalizeViewMode(o.viewMode),
    };
  }

  // v1: mainDetailTab
  const v1 = o as Partial<ProjectDetailTabStateV1>;
  if (v1.mainDetailTab && MAIN_TABS.has(v1.mainDetailTab)) {
    return {
      activeTabId: v1.mainDetailTab,
      engineeringSubTab: ENGINEERING_SUB_TABS.has(v1.engineeringSubTab as ProjectDetailEngineeringSubTab)
        ? (v1.engineeringSubTab as ProjectDetailEngineeringSubTab)
        : 'requirements',
      dynamicTabs: [],
      viewMode: 'task',
    };
  }

  return null;
}

function migrateLegacyIfNeeded(): Record<string, ProjectDetailTabState> {
  const current = readRawMap(STORAGE_KEY);
  const out: Record<string, ProjectDetailTabState> = {};
  for (const [pid, raw] of Object.entries(current)) {
    const n = normalizeState(raw);
    if (n) out[pid] = n;
  }
  if (Object.keys(out).length > 0) return out;

  for (const key of [LEGACY_V2_KEY, LEGACY_V1_KEY]) {
    const legacy = readRawMap(key);
    for (const [pid, raw] of Object.entries(legacy)) {
      const n = normalizeState(raw);
      if (n && !out[pid]) out[pid] = n;
    }
  }
  if (Object.keys(out).length > 0) {
    writeMap(out);
    try {
      sessionStorage.removeItem(LEGACY_V2_KEY);
      sessionStorage.removeItem(LEGACY_V1_KEY);
    } catch {
      // ignore
    }
  }
  return out;
}

export function getProjectDetailTabState(projectId: number): ProjectDetailTabState | null {
  const map = migrateLegacyIfNeeded();
  return map[String(projectId)] ?? null;
}

export function saveProjectDetailTabState(projectId: number, state: ProjectDetailTabState) {
  const normalized = normalizeState(state);
  if (!normalized) return;
  const map = migrateLegacyIfNeeded();
  map[String(projectId)] = normalized;
  writeMap(map);
}

export function clearProjectDetailTabState() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(LEGACY_V2_KEY);
    sessionStorage.removeItem(LEGACY_V1_KEY);
  } catch {
    // ignore
  }
}

/** 模式切换时映射激活 Tab，避免丢上下文 */
export function mapActiveTabForViewMode(
  activeTabId: string,
  engineeringSubTab: ProjectDetailEngineeringSubTab,
  nextMode: ProjectViewMode,
): { activeTabId: string; engineeringSubTab: ProjectDetailEngineeringSubTab } {
  if (nextMode === 'task') {
    if (activeTabId === 'engineering') {
      return { activeTabId: engineeringSubTab, engineeringSubTab };
    }
    return { activeTabId, engineeringSubTab };
  }
  // management
  if (isProjectTaskModeEngTab(activeTabId)) {
    return { activeTabId: 'engineering', engineeringSubTab: activeTabId };
  }
  return { activeTabId, engineeringSubTab };
}

/**
 * 定位到工程模块的指定子页（AI 跳转等场景）。
 * 保留已有的动态 Tab 与视图模式，并按当前模式换算激活 Tab。
 */
export function saveProjectDetailEngineeringTab(
  projectId: number,
  engineeringSubTab: ProjectDetailEngineeringSubTab,
) {
  const prev = getProjectDetailTabState(projectId);
  const viewMode = prev?.viewMode ?? 'task';
  const mapped = mapActiveTabForViewMode('engineering', engineeringSubTab, viewMode);
  saveProjectDetailTabState(projectId, {
    activeTabId: mapped.activeTabId,
    engineeringSubTab: mapped.engineeringSubTab,
    dynamicTabs: prev?.dynamicTabs ?? [],
    viewMode,
  });
}
